import { Injectable, computed, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { RbacService } from '../../Modules/Rbac/Services/rbac.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { PermissionGrant } from '../../Models/backoffice/permission-grant.model';
import { RoleSummary } from '../../Models/backoffice/role-summary.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import {
  PermissionCatalogueResponse,
  RoleListResponse,
  RoleShowResponse,
} from '../../Interfaces/api/backoffice/rbac.interface';

@Injectable()
export class RbacFacade {
  private readonly _rbacService = inject(RbacService);
  private readonly _resourceState = inject(ResourceStateService);

  private readonly _search = signal<string>('');
  private readonly _organizationId = signal<string>('');
  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _selectedRoleId = signal<string>('');

  private readonly _roles = httpResource<LaravelDefaultResponse<RoleListResponse>>(() =>
    this._rbacService.rolesRequest(
      this._search(),
      this._organizationId(),
      this._page(),
      this._pageSize(),
    ),
  );

  private readonly _role = httpResource<LaravelDefaultResponse<RoleShowResponse>>(() =>
    this._selectedRoleId() ? this._rbacService.roleRequest(this._selectedRoleId()) : undefined,
  );

  private readonly _permissions = httpResource<LaravelDefaultResponse<PermissionCatalogueResponse>>(
    () => this._rbacService.permissionsRequest(),
  );

  readonly search = computed<string>(() => this._search());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly selectedRoleId = computed<string>(() => this._selectedRoleId());

  readonly roles = computed<RoleSummary[]>(
    () => this._rbacService.toRolesPage(this._resourceState.valueOf(this._roles)).items,
  );

  readonly total = computed<number>(() => this._rbacService.toRolesPage(this._resourceState.valueOf(this._roles)).total);

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._roles.status()),
  );

  readonly isEmpty = computed<boolean>(() => this.state() === 'ready' && this.roles().length === 0);

  readonly selectedRole = computed<RoleSummary | null>(() =>
    this._rbacService.toRoleDetail(this._resourceState.valueOf(this._role)),
  );

  readonly roleState = computed<MetricState>(() =>
    this._resourceState.metricState(this._role.status()),
  );

  readonly permissions = computed<PermissionGrant[]>(() =>
    this._rbacService.toPermissionCatalogue(this._resourceState.valueOf(this._permissions)),
  );

  readonly permissionsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._permissions.status()),
  );

  readonly scopeablePermissions = computed<PermissionGrant[]>(() =>
    this.permissions().filter((permission) => permission.is_scopeable),
  );

  applySearch(search: string): void {
    this._search.set(search);
    this._page.set(FIRST_PAGE);
  }

  filterByOrganization(organizationId: string): void {
    this._organizationId.set(organizationId);
    this._page.set(FIRST_PAGE);
  }

  selectRole(roleId: string): void {
    this._selectedRoleId.set(this._selectedRoleId() === roleId ? '' : roleId);
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._roles.reload();
    this._permissions.reload();
  }
}
